import React, { useState } from "react";

import IconButton from "@material-ui/core/IconButton";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import routes from "common/routes";
import FacilityMarkerIndicator from "components/FacilityMarkerIndicator";
import Link from "components/Link";
import ScheduleDateMenu from "components/ScheduleDateMenu";
import { useSnackbar } from "components/SnackbarContext/SnackbarContext";
import { useRacehorse360Api } from "hooks/api";
import { TUpcomingActivity } from "../../types";
import { getUpcomingActivityType } from "./helper";

import useStyles from "./styles";

export interface Props {
  upcomingActivity: TUpcomingActivity;
  refetchWorkoutRequests: () => void;
}

const ActivityItem = (props: Props) => {
  const { upcomingActivity, refetchWorkoutRequests } = props;

  const classes = useStyles();

  const { showErrorSnack } = useSnackbar();

  const [anchorEl, setAnchorEl] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { useCancelWorkoutRequest, useMoveFacilityWorkoutRequest } =
    useRacehorse360Api();

  const {
    isPending: isWorkoutRequestCancelling,
    mutateAsync: cancelWorkoutRequest
  } = useCancelWorkoutRequest();
  const {
    isPending: isWorkoutRequestMoving,
    mutateAsync: moveFacilityWorkoutRequest
  } = useMoveFacilityWorkoutRequest();

  const { isWorkoutRequest, isUpcomingRaceEntry } =
    getUpcomingActivityType(upcomingActivity);
  const horseName = upcomingActivity.horse?.name;

  const handleWorkoutClick =
    (workout: racehorse360.IWorkoutRequest) =>
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (workout.horse) {
        setAnchorEl(event.currentTarget);
        setIsMenuOpen(true);
      }
    };

  const handleWorkoutChange = (workout: racehorse360.IWorkoutRequest) => {
    if (workout.facility) {
      moveFacilityWorkoutRequest({
        id: workout.id,
        facilityId: workout.facility.id
      })
        .then(() => {
          refetchWorkoutRequests();
        })
        .catch(error => console.error(error));
    } else {
      cancelWorkoutRequest({
        id: workout.id
      })
        .catch(error => showErrorSnack(error.toString()))
        .then(() => {
          refetchWorkoutRequests();
        })
        .catch(error => console.error(error));
    }
  };

  const handleMenuClose = () => {
    setIsMenuOpen(false);
  };

  const renderDescription = () => {
    const facilityName = upcomingActivity.facility?.name;

    if (isWorkoutRequest) {
      return `Workout at ${facilityName}`;
    } else if (isUpcomingRaceEntry) {
      return `Race ${
        (upcomingActivity as racehorse360.UpcomingRaceEntry)?.raceNumber
      } at ${facilityName}`;
    }
  };

  return (
    <>
      <div key={upcomingActivity.id} className={classes.row}>
        <div className={classes.marker}>
          <FacilityMarkerIndicator
            facility={upcomingActivity.facility}
            size={4}
          />
        </div>
        <div className={classes.activityInfo}>
          <Link
            className={classes.activityInfoName}
            to={routes.horseDetails.path.replace(
              ":horseId",
              String(upcomingActivity?.horse?.id)
            )}
          >
            {horseName}
          </Link>
          <span className={classes.activityInfoContent}>
            {renderDescription()}
          </span>
        </div>
        {isWorkoutRequest && (
          <IconButton
            className={classes.button}
            disabled={isWorkoutRequestCancelling || isWorkoutRequestMoving}
            onClick={handleWorkoutClick(
              upcomingActivity as racehorse360.IWorkoutRequest
            )}
          >
            <MoreHorizIcon />
          </IconButton>
        )}
      </div>
      {isWorkoutRequest && isMenuOpen && (
        <ScheduleDateMenu
          isOpen
          anchorEl={anchorEl}
          horse={upcomingActivity.horse}
          workoutData={upcomingActivity as racehorse360.IWorkoutRequest}
          onChange={handleWorkoutChange}
          onClose={handleMenuClose}
        />
      )}
    </>
  );
};

export default React.memo(ActivityItem);
